<template>
    <MediaQueryProvider :queries="$options.queries" ssr>
        <div
            id="catalog-vue"
            class="catalog-vue"
            :data-redirect-to-page="redirectToPage"
        >
            <LazyHydrate never>
                <div
                    v-if="isSearchPage"
                    id="div-gpt-ad-1676540049228-0"
                    class="dfpAd search"
                />
                <div
                    v-else
                    id="div-gpt-ad-LEADBOARD-0"
                    class="dfpAd top-banner"
                />
            </LazyHydrate>
            <div class="catalog-ad">
                <div class="breadcrumbs-desktop-container"></div>
            </div>
            <Layout :is-loading="isLoading">
                <template
                    v-if="
                        !category.isContentMode &&
                        category.layout !== 'one_column'
                    "
                    #aside
                >
                    <LazyHydrate when-idle>
                        <div>
                            <div class="aside-filters">
                                <CategoryTree
                                    v-if="!isSearchPage && categoryList"
                                    :category-list="categoryList"
                                />

                                <FilterList
                                    v-if="filters"
                                    :filters="filters"
                                    :category-tree="category.categoryTree"
                                    :category-path="category.path"
                                />
                            </div>
                        </div>
                    </LazyHydrate>
                </template>
                <div :class="{ noProducts: isCategoryEmpty }">
                    <CategoryContent
                        :category="category"
                        :is-search-page="isSearchPage"
                        :params="params"
                        :query-string="queryString"
                    />
                    <template v-if="!category.isContentMode">
                        <MatchMedia v-slot="{ mobile }">
                            <template v-if="mobile">
                                <LazyHydrate when-visible>
                                    <FilterListMobile
                                        :trigger-mobile-filter="
                                            triggerMobileFilter
                                        "
                                        :trigger-data-filter-name="
                                            triggerDataFilterName
                                        "
                                    >
                                        <template #before-filters>
                                            <CategoryTree
                                                v-if="
                                                    !isSearchPage &&
                                                    categoryList
                                                "
                                                :category-list="categoryList"
                                            />
                                        </template>
                                    </FilterListMobile>
                                </LazyHydrate>
                            </template>
                        </MatchMedia>
                        <LazyHydrate when-idle>
                            <Pager>
                                <template #left>
                                    <FilterListActive
                                        v-if="Object.keys(filtersActive).length"
                                        :filters-active="getFiltersActive"
                                        :is-mobile="isMobile"
                                        @chooseMobileFilter="chooseMobileFilter"
                                    />
                                </template>
                                <template #right>
                                    <CatalogSort class="hidden-mobile" />
                                    <Limiter />
                                    <div class="view-toggle">
                                        <button
                                            :class="{
                                                active: viewMode === 'grid'
                                            }"
                                            aria-label="Kafelkowy układ produktów"
                                            @click="setViewMode('grid')"
                                        >
                                            <GridListIcon
                                                width="30"
                                                height="30"
                                                aria-hidden="true"
                                            />
                                        </button>
                                        <button
                                            :class="{
                                                active: viewMode === 'list'
                                            }"
                                            aria-label="Kolumnowy układ produktów"
                                            @click="setViewMode('list')"
                                        >
                                            <RowListIcon
                                                width="30"
                                                height="30"
                                                aria-hidden="true"
                                            />
                                        </button>
                                    </div>
                                </template>
                            </Pager>
                        </LazyHydrate>
                        <LazyHydrate when-idle>
                            <div class="filters-quick-container">
                                <FilterListQuick
                                    v-if="
                                        filters.quick_filters &&
                                        !Object.keys(filtersActive).length
                                    "
                                    :filters-quick="filters.quick_filters"
                                />
                            </div>
                        </LazyHydrate>

                        <LazyHydrate when-idle>
                            <ProductList
                                :products="products"
                                :is-search-page="isSearchPage"
                                :view-mode="viewMode"
                            >
                                <LazyHydrate never>
                                    <template v-if="!isLoading">
                                        <p class="note-msg">
                                            {{ i18n('No Items Found') }}
                                        </p>
                                    </template>
                                    <template v-else-if="isSearchPage">
                                        <p class="note-msg">
                                            {{ i18n('Searching in progress') }}
                                            {{ i18n('for') }}:
                                            {{ params.q }}
                                        </p>
                                    </template>
                                </LazyHydrate>
                            </ProductList>
                        </LazyHydrate>
                        <LazyHydrate when-idle>
                            <div
                                v-if="products && products.length"
                                class="pagination-section"
                            >
                                <div class="pagination-state-container">
                                    <PaginationState />
                                </div>

                                <PaginationList />
                            </div>
                        </LazyHydrate>

                        <div
                            v-if="
                                category.cloudTags &&
                                category.cloudTags.length > 0
                            "
                            class="catalogTagCloud"
                        >
                            <span
                                v-for="cloudTag in category.cloudTags"
                                :key="cloudTag.key"
                                class="cloud-tag button"
                            >
                                <a :href="cloudTag.url">{{ cloudTag.name }}</a>
                            </span>
                        </div>
                    </template>
                </div>
            </Layout>
        </div>
    </MediaQueryProvider>
</template>
<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
import { MediaQueryProvider, MatchMedia } from 'vue-component-media-queries'
import LazyHydrate from 'vue-lazy-hydration'
import resizeObserver from '@vue/utils/resizeObserver.js'
import Layout from './components/Layout'
import {
    FilterListActive,
    FilterList,
    FilterListMobile,
    FilterListQuick
} from './components/FilterList'
import ProductList from './components/ProductList'
import Pager from './components/Pager'
import PaginationList, { PaginationState } from './components/PaginationList'
import CatalogSort from './components/CatalogSort'
import { CategoryTree, CategoryContent } from './components/Category'
import breakpoints from '../../js/utils/rwd'
import Limiter from './components/Limiter'
import GridListIcon from '@icons/tile_list.svg'
import RowListIcon from '@icons/row_list.svg'

const queries = {
    mobile: breakpoints.mq.toSm,
    desktop: breakpoints.mq.mdUp
}

export default {
    name: 'App',
    queries,
    components: {
        MediaQueryProvider,
        MatchMedia,
        Layout,
        FilterList,
        FilterListActive,
        FilterListMobile,
        FilterListQuick,
        ProductList,
        Pager,
        PaginationList,
        PaginationState,
        CatalogSort,
        CategoryTree,
        CategoryContent,
        LazyHydrate,
        Limiter,
        GridListIcon,
        RowListIcon
    },
    mixins: [resizeObserver],
    data() {
        return {
            isOpen: false,
            triggerMobileFilter: '',
            triggerDataFilterName: '',
            viewMode: 'grid'
        }
    },
    computed: {
        ...mapState([
            'products',
            'filters',
            'filtersActive',
            'params',
            'noProductsBlock',
            'categoryFaq',
            'categoryDescr',
            'redirectToPage'
        ]),
        ...mapState({
            queryString: (state) => state.queryString,
            category: ({ options }) => options?.category,
            isLoading: ({ ui }) => ui?.isLoading,
            categoryList: ({ category }) => category
        }),
        ...mapGetters([
            'getFiltersActive',
            'i18n',
            'isSearchPage',
            'sortedCategoryList'
        ]),
        isCategoryEmpty() {
            return this.noProductsBlock !== undefined
        },
        faqContent() {
            if (this.categoryFaq === null) {
                return []
            }

            if (
                typeof this.categoryFaq === 'object' &&
                Object.keys(this.categoryFaq).length > 0 &&
                Object.prototype.hasOwnProperty.call(
                    this.categoryFaq,
                    'questions'
                )
            ) {
                const parsedFaq = JSON.parse(this.categoryFaq.questions)
                return Object.values(parsedFaq).sort(
                    (a, b) => a.order - b.order
                )
            }

            return []
        }
    },

    async mounted() {
        document.documentElement.addEventListener(
            'aoestatic_afterblockreplace',
            () => {
                const formKey = document.querySelector('#catalog_formkey input')
                if (formKey) {
                    const formKeyValue = formKey.value
                    this.setFormKey(formKeyValue)
                }
            }
        )
        if (!this.products) {
            await this.getProducts()
        }
        const pageUrl = window.location.pathname
        this.setPathname(pageUrl)
        this.callAdScripts()

        this.$nextTick(function () {
            window.addEventListener('scroll', this.onScroll)
            this.scrollIntoView()

            if (parseInt(this.params.p) !== 1) {
                document.querySelector('.category-faq').style.display = 'none'
            } else {
                document.querySelector('.category-faq').style.display = 'block'
            }
        })
    },
    methods: {
        ...mapActions(['getProducts']),
        ...mapMutations({
            setFormKey: 'SET_FORMKEY',
            setPathname: 'SET_PATHNAME'
        }),
        setViewMode(mode) {
            this.viewMode = mode
        },
        callAdScripts() {
            if (this.isSearchPage) {
                window.googletag.cmd.push(function () {
                    window.googletag.display('div-gpt-ad-1676540049228-0')
                })
                window.googletag.cmd.push(function () {
                    window.googletag.display('div-gpt-ad-1676540201424-0')
                })
            }
        },
        onScroll() {
            const offsetTop = document.documentElement.scrollTop
            localStorage.setItem(
                'searchPageOffset',
                JSON.stringify({ [document.URL]: offsetTop })
            )
        },
        scrollIntoView() {
            const searchPageOffset = JSON.parse(
                localStorage.getItem('searchPageOffset')
            )
            if (searchPageOffset[document.URL] !== 'undefined') {
                history.scrollRestoration = 'manual'
                document.documentElement.scrollTop =
                    searchPageOffset[document.URL]
            } else if (window.scrollY > 400) {
                document.querySelector('.breadcrumbs__wrapper').scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                })
            }
        },
        chooseMobileFilter(filterIndex) {
            this.triggerMobileFilter = Date.now() || 0
            this.triggerDataFilterName = filterIndex
        }
    }
}
</script>
<style scoped lang="scss">
.catalog-vue {
    .top-banner {
        height: 180px;
        margin: 10px -15px;

        @media #{$media-xs} {
            margin: 0 0 15px 0;
            height: 120px;
        }
    }

    .aside-filters {
        background: $gray-light;
        padding: 20px;
    }

    .aside-banner {
        background: $gray-dark;
        height: 600px;
        width: 300px;
        margin: 10px auto;
    }

    &__buttons {
        column-gap: 2rem;

        @media #{$media-md-up} {
            display: none !important;
        }

        @media #{$media-to-sm} {
            display: flex;
        }

        .btn {
            flex: 1;

            span {
                margin-right: auto;
                margin-left: 1rem;
            }
        }
    }

    .noProducts {
        margin-left: 20px;
        @media #{$media-to-sm} {
            margin-left: 0;
        }

        .catalog-pager,
        .filters-quick-container,
        .note-msg,
        .max-price,
        .filter-mobile {
            display: none;
        }
    }

    .view-toggle {
        display: flex;

        button {
            border: none;
            display: inline-flex;
            border-radius: 0px;
            margin: 0 2px;
            padding: 2px;
            min-height: 24px;
            min-width: 24px;
            font-size: 16px;
            text-transform: none;
            font-weight: 600;
            width: 28px;
            height: 28px;
            background: transparent;
            cursor: pointer;

            &.active {
                fill: #007f32;
                stroke: #007f32;
                color: #007f32;

                path {
                    fill: #007f32;
                    stroke: #007f32;
                    color: #007f32;
                }
            }
        }
    }

    button:focus,
    a:focus,
    input:focus,
    select:focus,
    textarea:focus,
    [tabindex]:focus {
        outline: 5px auto Highlight !important;
        outline: 5px auto -webkit-focus-ring-color !important;
    }
}

.catalog-category-view {
    .category-description {
        margin-top: 80px;

        @media #{$media-to-sm} {
            margin-top: 40px;
        }
    }
}

.pagination-section {
    position: relative;
    @media #{$media-to-sm} {
        display: flex;
        flex-direction: column;
        gap: 15px;
        align-items: center;
    }
}
.pagination-state-container {
    position: initial;
    @media #{$media-to-sm} {
        position: relative;
    }
}
.catalogTagCloud {
    margin-top: 32px;
    display: flex;
    flex-wrap: wrap;
    gap: 16px;

    .cloud-tag {
        background-color: #fff;
        border-radius: 25px;
        height: 40px;
        min-height: 40px;
        width: fit-content;
        min-width: auto;
        padding: 0 16px;
        font-size: 14px;
        line-height: 22px;
        border: 0.5px solid $btn-default-border;
        font-weight: 600;
        justify-content: center;
        margin-bottom: 0;
        transition: background-color 0.1s ease-in-out;
        align-items: center;
        display: grid;

        a {
            color: #2f3146;
        }

        &:hover {
            background-color: $btn-default-hover;
            border-color: $btn-default-hover;

            a {
                color: #fff;
            }
        }
    }
}
</style>
